/**
 * The `base44` object, reimplemented against the Westwood API.
 *
 * This file keeps its original name and export on purpose. Thirty-one modules
 * across the app do `import { base44 } from '@/api/base44Client'` and then call
 * base44.entities.Project.filter(...) or base44.auth.me(). Reproducing that
 * surface exactly is what let the entire UI move to Azure without being
 * rewritten - the Base44 SDK is gone, its shape is not.
 *
 * Supported, matching the original call signatures:
 *   base44.entities.<Entity>.list(sort?, limit?)
 *   base44.entities.<Entity>.filter(query, sort?, limit?)
 *   base44.entities.<Entity>.get(id)
 *   base44.entities.<Entity>.create(data) / .bulkCreate(array)
 *   base44.entities.<Entity>.update(id, data) / .delete(id)
 *   base44.auth.me() / .logout(returnTo?) / .redirectToLogin(returnTo?) / .isAuthenticated()
 *   base44.users.inviteUser(email, role, { full_name, job_title }?)
 *   base44.functions.invoke(name, payload)
 *   base44.integrations.Core.UploadFile({ file, project_id })  - or { file, contractor_id }
 *   base44.integrations.Core.InvokeLLM({ prompt })
 *   base44.agents.listConversations / createConversation / getConversation /
 *                 addMessage / subscribeToConversation
 *
 * One signature changed: UploadFile now needs `project_id`. Files live in a
 * per-project container path and access is checked against the project on every
 * download, so an upload has to say which project it belongs to. All nine call
 * sites already had the id in scope.
 */

import { getAccessToken, getAccount, login, logout as msalLogout } from '@/lib/msal';

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');

const ENTITY_NAMES = [
  'Project',
  'Task',
  'TaskComment',
  'Milestone',
  'PlanSubmission',
  'ProjectFile',
  'DailyReport',
  'WorkflowStep',
  'WorkflowDocument',
  'WorkflowReview',
  'ActivityLog',
  'Suggestion',
  'User',
  /* Not a Base44 entity. Westwood's properties moved out of a hand-typed
     source file and into the database in migration 009; this is what the
     Asset Name dropdown and the Properties page read. */
  'Property',
  /* Also not Base44's. The folders on a project's Files tab and the document
     types under each, maintained on the Document Types page (migration 017). */
  'DocumentCategory',
  'DocumentType',
  /* The state lien waiver forms - links into SharePoint, kept on the Lien
     Waiver Forms page (migration 023). */
  'LienWaiverForm',
  /* Contractors, kept once each, and their certificates of insurance per
     property, on the Contractors page (migration 027). */
  'Contractor',
  'ContractorCertificate',
  /* The punch list and closeout checklist on a capital or MOE project
     (migration 028). */
  'PunchItem',
  /* Questions on a plan set or sign package, and the replies under them
     (migration 029). */
  'PlanQuestion',
];

/** Error shaped like the SDK's, so existing `err.status` checks still work. */
export class ApiError extends Error {
  constructor(status, message, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

async function request(path, { method = 'GET', body, query } = {}) {
  const token = await getAccessToken();
  const url = new URL(`${API_BASE}/api${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query || {})) {
    if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
  }

  const response = await fetch(url.toString(), {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
  });

  if (response.status === 204) return null;

  const text = await response.text();
  const data = text ? safeJson(text) : null;

  if (!response.ok) {
    throw new ApiError(response.status, data?.error || response.statusText, data);
  }
  return data;
}

function safeJson(text) {
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

/* ----------------------------------------------------------- entities --- */

function entityClient(name) {
  return {
    list: (sort, limit) => request(`/entities/${name}`, { query: { sort, limit } }),

    filter: (query, sort, limit) =>
      request(`/entities/${name}`, {
        query: { filter: JSON.stringify(query || {}), sort, limit },
      }),

    get: async (id) => {
      const rows = await request(`/entities/${name}`, {
        query: { filter: JSON.stringify({ id }), limit: 1 },
      });
      return rows?.[0] ?? null;
    },

    create: (data) => request(`/entities/${name}`, { method: 'POST', body: data }),

    bulkCreate: (items) =>
      request(`/entities/${name}/bulk`, { method: 'POST', body: { items } }),

    update: (id, data) => request(`/entities/${name}/${id}`, { method: 'PATCH', body: data }),

    delete: (id) => request(`/entities/${name}/${id}`, { method: 'DELETE' }),
  };
}

const entities = Object.fromEntries(ENTITY_NAMES.map((name) => [name, entityClient(name)]));

/* --------------------------------------------------------------- auth --- */

let cachedUser = null;

const auth = {
  async me() {
    /* Cached for the page's lifetime: several components call me() on mount
       and the answer cannot change without a new sign-in. */
    if (!cachedUser) cachedUser = await request('/me');
    return cachedUser;
  },

  /**
   * Change how often this person is emailed: 'daily' (the default - one
   * summary each morning) or 'immediate' (one email per event, as it happens).
   *
   * Not part of the Base44 surface; there was no such setting there, because
   * there everything was immediate. The local cache is dropped afterwards so
   * the next me() reflects the change rather than the page-load value.
   */
  async setNotificationMode(mode) {
    const result = await request('/me/notifications', {
      method: 'PATCH',
      body: { notification_mode: mode },
    });
    cachedUser = null;
    return result;
  },

  isAuthenticated() {
    return Boolean(getAccount());
  },

  redirectToLogin(returnTo) {
    return login(returnTo);
  },

  logout(returnTo) {
    cachedUser = null;
    return msalLogout(returnTo);
  },

  clearCache() {
    cachedUser = null;
  },
};

/* -------------------------------------------------------------- users --- */

const users = {
  /* `details` is an addition, not a change of shape: { full_name, job_title },
     both optional. The API refuses a role the title does not allow - see
     api/src/lib/userAccess.ts. */
  inviteUser: (email, role, details = {}) =>
    request('/users/invite', {
      method: 'POST',
      body: { email, role, full_name: details.full_name, job_title: details.job_title },
    }),
};

/* ---------------------------------------------------------- functions --- */

const functions = {
  invoke: (name, payload = {}) =>
    request(`/functions/${name}`, { method: 'POST', body: payload }),
};

/* ------------------------------------------------------- integrations --- */

/**
 * Upload a file.
 *
 * Two steps: ask the API to sign a short-lived upload URL, then PUT the bytes
 * straight to blob storage. Large plan sets never pass through the API, and the
 * returned file_url is a permanent app link rather than an expiring signature.
 *
 * A contractor's certificate of insurance belongs to no one project, so it
 * names `contractor_id` instead and is stored under that contractor's folder
 * (migration 027).
 */
async function UploadFile({ file, project_id, contractor_id }) {
  if (!file) throw new Error('UploadFile requires a file');
  if (!project_id && !contractor_id) throw new Error('UploadFile requires a project_id');

  const { upload_url, file_url, blob_path } = await request('/files/sas', {
    method: 'POST',
    body: contractor_id ? { contractor_id, file_name: file.name } : { project_id, file_name: file.name },
  });

  const upload = await fetch(upload_url, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });

  if (!upload.ok) {
    throw new ApiError(upload.status, `Upload failed: ${upload.statusText}`);
  }

  return { file_url, blob_path, file_name: file.name };
}

const integrations = {
  Core: {
    UploadFile,
    InvokeLLM: ({ prompt, system_prompt }) =>
      request('/functions/invokeLLM', { method: 'POST', body: { prompt, system_prompt } }),
  },
};

/* ------------------------------------------------------------- agents --- */

const AGENT_POLL_MS = 2000;

const agents = {
  listConversations: ({ agent_name }) =>
    request('/agents/conversations', { query: { agent_name } }),

  createConversation: ({ agent_name, metadata }) =>
    request('/agents/conversations', { method: 'POST', body: { agent_name, metadata } }),

  getConversation: (id) => request(`/agents/conversations/${id}`),

  /**
   * Post a message and wait for the reply.
   *
   * The API generates the assistant turn inline, so this resolves with the
   * updated conversation. Any subscriber is notified on the next poll.
   */
  addMessage: (conversation, { content }) =>
    request(`/agents/conversations/${conversation.id}/messages`, {
      method: 'POST',
      body: { content },
    }),

  /**
   * Poll a conversation and call back when it changes.
   *
   * Base44 pushed updates over a socket. Polling every two seconds is enough
   * for a chat panel that only changes when the person using it sends
   * something, and it has no connection to keep alive.
   */
  subscribeToConversation(conversationId, onUpdate) {
    let stopped = false;
    let lastCount = -1;

    const tick = async () => {
      if (stopped) return;
      try {
        const convo = await request(`/agents/conversations/${conversationId}`);
        const count = convo?.messages?.length ?? 0;
        if (count !== lastCount || convo?.status === 'running') {
          lastCount = count;
          onUpdate(convo);
        }
      } catch {
        /* Transient failures are ignored; the next tick tries again. */
      }
      if (!stopped) timer = setTimeout(tick, AGENT_POLL_MS);
    };

    let timer = setTimeout(tick, 0);

    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  },
};

/* ---------------------------------------------------------- downloads --- */

/**
 * Ask the API for a short-lived signed link to one document.
 *
 * This is how the app opens documents. It sends the access token in a header -
 * the one credential that cannot be dropped in transit, rejected by a cookie
 * jar or left behind when the token is refreshed - and gets back a URL that
 * goes straight to storage. Access is still checked against the project on
 * every call, and the signature is short-lived, so nothing about the guarantee
 * changes; only the way the caller proves who they are.
 */
export async function getDownloadUrl(blobPath) {
  const { url } = await request('/files/download', { query: { path: blobPath, mode: 'link' } });
  return url;
}

/**
 * Plant the download cookie, for links that were opened outside the app.
 *
 * The app itself does not use this cookie. Every document link in it goes
 * through toDownloadUrl to /OpenDocument, which asks getDownloadUrl for a
 * signed URL with the token in a header (PR #22, 2026-09-17).
 *
 * The cookie is only ever read by /api/files/download when someone follows an
 * older link from an email or an exported report PDF - a plain href that
 * carries no Authorization header. If the cookie is missing or has expired,
 * the API redirects that person to /OpenDocument instead, so a lapsed cookie
 * costs one extra hop, not a failed download. That is why this is called once
 * after sign-in and never renewed.
 */
export async function startDownloadSession() {
  try {
    await request('/session', { method: 'POST', body: {} });
    return true;
  } catch {
    return false;
  }
}

export const base44 = {
  entities,
  auth,
  users,
  functions,
  integrations,
  agents,
};

export default base44;
